import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import LoadingSpinner from '../components/LoadingSpinner';
import ProductCarousel from '../components/affiliate/ProductCarousel';
import ReactMarkdown from 'react-markdown';
import api from '../api/api';
import '../styles/pages/RecipeDetailPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://hashimoto-backend01.onrender.com';

/**
 * RecipeDetailPage - 認証付きレシピ詳細ページ
 * つむぎデザインシステム準拠（BEM記法）
 * DEPRESSION_MODE_REDESIGN.md準拠: おやすみモード時は栄養情報・共有ボタン非表示
 */
const RecipeDetailPage = () => {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { depressionMode } = useDepressionMode();

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userNote, setUserNote] = useState('');
  const [isEditingNote, setIsEditingNote] = useState(false);

  // つむぎチャットから来た場合の戻り先を取得
  const backTo = sessionStorage.getItem('recipeBackTo');
  const tsumugiSessionId = sessionStorage.getItem('tsumugiSessionId');
  const backPath = backTo === 'tsumugi-chat' && tsumugiSessionId
    ? `/chat/${tsumugiSessionId}`
    : '/recipes';
  const backLabel = backTo === 'tsumugi-chat' ? 'つむぎチャット' : '保存レシピ一覧';

  useEffect(() => {
    fetchRecipe();

    // クリーンアップ: ページを離れる時にsessionStorageをクリア
    return () => {
      sessionStorage.removeItem('recipeBackTo');
      sessionStorage.removeItem('tsumugiSessionId');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/recipes/${recipeId}`);

      if (response.data.success) {
        setRecipe(response.data.recipe);
        setUserNote(response.data.recipe.userNote || '');
        document.title = `${response.data.recipe.title} | はしもと栄養パートナー`;
      } else {
        alert('❌ レシピの取得に失敗しました');
        navigate('/recipes');
      }
    } catch (error) {
      console.error('レシピ取得エラー:', error);
      alert('❌ レシピの取得に失敗しました');
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    try {
      const response = await api.patch(`/api/recipes/${recipeId}/favorite`);

      if (response.data.success) {
        setRecipe({ ...recipe, isFavorite: !recipe.isFavorite });
      } else {
        alert('❌ お気に入りの更新に失敗しました');
      }
    } catch (error) {
      console.error('お気に入り更新エラー:', error);
      alert('❌ お気に入りの更新に失敗しました');
    }
  };

  const saveNote = async () => {
    try {
      const response = await api.patch(`/api/recipes/${recipeId}/note`, { note: userNote });

      if (response.data.success) {
        setRecipe({ ...recipe, userNote });
        setIsEditingNote(false);
        alert('✅ メモを保存しました');
      } else {
        alert('❌ メモの保存に失敗しました');
      }
    } catch (error) {
      console.error('メモ保存エラー:', error);
      alert('❌ メモの保存に失敗しました');
    }
  };

  const handleShare = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recipes/${recipeId}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        const shareUrl = data.shareUrl;

        if (navigator.share) {
          await navigator.share({
            title: recipe.title,
            text: `「${recipe.title}」のレシピをチェック！`,
            url: shareUrl
          });
        } else {
          await navigator.clipboard.writeText(shareUrl);
          alert(`📋 共有URLをコピーしました！\n${shareUrl}`);
        }
      } else {
        alert('❌ 共有URLの作成に失敗しました');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('シェアに失敗しました:', err);
        alert('❌ シェアに失敗しました');
      }
    }
  };

  const deleteRecipe = async () => {
    if (!window.confirm('このレシピを削除しますか？')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/recipes/${recipeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ レシピを削除しました');
        navigate('/recipes');
      } else {
        alert('❌ レシピの削除に失敗しました');
      }
    } catch (error) {
      console.error('レシピ削除エラー:', error);
      alert('❌ レシピの削除に失敗しました');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageTitle title="レシピ詳細" />
        <LoadingSpinner message="レシピを読み込んでいます..." size="medium" />
      </AppLayout>
    );
  }

  if (!recipe) {
    return null;
  }

  return (
    <AppLayout>
      <PageTitle
        title={recipe.title}
        subtitle="レシピ詳細"
        backPath={backPath}
        backLabel={backLabel}
        actionButton={
          !depressionMode ? (
          <button
            className={`recipe-detail-page__favorite-btn ${recipe.isFavorite ? 'recipe-detail-page__favorite-btn--active' : ''}`}
            onClick={toggleFavorite}
            title={recipe.isFavorite ? 'ピン留めを外す' : 'ピン留めする'}
          >
            📌
          </button>
          ) : null
        }
      />

      <div className="recipe-detail-page">
        <div className="recipe-detail-page__container">
          {/* タグ - おやすみモードでは非表示 */}
          {!depressionMode && recipe.tags && recipe.tags.length > 0 && (
            <div className="recipe-detail-page__tags">
              {recipe.tags.map((tag, idx) => (
                <span key={idx} className="recipe-detail-page__tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* レシピ本文 */}
          <div className="recipe-detail-page__content">
            <ReactMarkdown>
              {recipe.content}
            </ReactMarkdown>
          </div>

          {/* アフィリエイト商品カルーセル: レシピに関連する商品 */}
          <ProductCarousel location="recipe_page" />

          {/* メモセクション - おやすみモードでは非表示 */}
          {!depressionMode && (
          <div className="recipe-detail-page__note-section">
            <div className="recipe-detail-page__note-header">
              <h2 className="recipe-detail-page__note-title">📝 メモ <span className="recipe-detail-page__note-optional">（任意）</span></h2>
              {!isEditingNote && (
                <button
                  className="recipe-detail-page__note-edit-btn"
                  onClick={() => setIsEditingNote(true)}
                >
                  {userNote ? '編集' : '追加'}
                </button>
              )}
            </div>

            {isEditingNote ? (
              <div className="recipe-detail-page__note-edit">
                <textarea
                  className="recipe-detail-page__note-textarea"
                  value={userNote}
                  onChange={(e) => setUserNote(e.target.value)}
                  placeholder="作ってみた感想や、アレンジのアイデアなどを記録しましょう"
                  rows="5"
                />
                <div className="recipe-detail-page__note-actions">
                  <button
                    className="recipe-detail-page__note-save-btn"
                    onClick={saveNote}
                  >
                    保存
                  </button>
                  <button
                    className="recipe-detail-page__note-cancel-btn"
                    onClick={() => {
                      setUserNote(recipe.userNote || '');
                      setIsEditingNote(false);
                    }}
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            ) : (
              <div className="recipe-detail-page__note-display">
                {userNote ? (
                  <p className="recipe-detail-page__note-text">{userNote}</p>
                ) : (
                  <p className="recipe-detail-page__note-empty">メモはまだありません</p>
                )}
              </div>
            )}
          </div>
          )}

          {/* アクションボタン - おやすみモードでは非表示 */}
          {!depressionMode && (
          <div className="recipe-detail-page__actions">
            <button
              className="recipe-detail-page__action-btn recipe-detail-page__action-btn--share"
              onClick={handleShare}
            >
              📤 シェアする
            </button>
            <button
              className="recipe-detail-page__action-btn recipe-detail-page__action-btn--delete"
              onClick={deleteRecipe}
            >
              🗑️ 削除する
            </button>
          </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default RecipeDetailPage;
